import { useToast } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4, validate } from 'uuid';

import { readLocalStorage, writeLocalStorage } from '../utils/localStorage';

const LS_KEY_WIDGETS = 'LS_DASHBOARD_WIDGETS';
const LS_KEY_WIDGET_STATE = 'LS_KEY_WIDGET_STATE';

interface DashboardWidgetState {
    hiddenWidgets: string[];
    widgetOrder: string[];
}

const DEFAULT_DASHBOARD_WIDGET_STATE = {
    hiddenWidgets: [],
    widgetOrder: [],
};

export const useDashboardWidgets = () =>
    useQuery({
        queryKey: ['dashboardWidget', 'list'],
        queryFn: () => {
            let widgets = getDashboardWidgets();

            // generate UUID for existing widgets to ensure backward compatibility
            // TODO: remove this after all possible widget owners opened the site once ¯\_(ツ)_/¯
            if (!widgets.every((w) => validate(w.id))) {
                widgets = widgets.map((w) => ({ ...w, id: validate(w.id) ? w.id : uuidv4() }));
                writeLocalStorage(LS_KEY_WIDGETS, widgets);
            }

            return widgets;
        },
    });

export const useCreateDashboardWidget = () => {
    const toast = useToast();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (widgetDefinition: DashboardWidgetDefinition) => {
            const widgets = getDashboardWidgets();
            const newWidget = { ...widgetDefinition, id: uuidv4() };

            writeLocalStorage(LS_KEY_WIDGETS, [...widgets, newWidget]);
            return Promise.resolve();
        },
        onSuccess: () => {
            toast({
                title: 'Widget created',
                status: 'success',
                duration: 3000,
            });
            queryClient.invalidateQueries({ queryKey: ['dashboardWidget', 'list'] });
        },
    });
};

export const useUpdateDashboardWidget = () => {
    const toast = useToast();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (widgetDefinition: DashboardWidgetDefinition) => {
            const widgets = getDashboardWidgets();
            writeLocalStorage(
                LS_KEY_WIDGETS,
                widgets.map((w) => (w.id === widgetDefinition.id ? widgetDefinition : w)),
            );
            return Promise.resolve();
        },
        onSuccess: () => {
            toast({
                title: 'Widget updated',
                status: 'success',
                duration: 3000,
            });
            queryClient.invalidateQueries({ queryKey: ['dashboardWidget', 'list'] });
        },
    });
};

export const useRemoveDashboardWidget = () => {
    const toast = useToast();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (widgetId: string) => {
            const widgets = getDashboardWidgets();
            writeLocalStorage(
                LS_KEY_WIDGETS,
                widgets.filter(({ id }) => id !== widgetId),
            );
            return Promise.resolve();
        },
        onSuccess: () => {
            toast({
                title: 'Widget updated',
                status: 'success',
                duration: 3000,
            });
            queryClient.invalidateQueries({ queryKey: ['dashboardWidget', 'list'] });
        },
    });
};

export const useDashboardWidgetState = () =>
    useQuery({
        queryKey: ['dashboardWidget', 'state'],
        queryFn: () => getDashboardWidgetState(),
    });

export const useUpdateDashboardWidgetState = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (widgetState: DashboardWidgetState) => {
            writeLocalStorage(LS_KEY_WIDGET_STATE, widgetState);
            return Promise.resolve();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dashboardWidget', 'state'] });
        },
    });
};

const getDashboardWidgets = () => readLocalStorage<DashboardWidgetDefinition[]>(LS_KEY_WIDGETS, []);
const getDashboardWidgetState = () =>
    readLocalStorage<DashboardWidgetState>(LS_KEY_WIDGET_STATE, DEFAULT_DASHBOARD_WIDGET_STATE);
